import { CrossIcon } from "@solomon-labs/design/components/icons";
import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { Button } from "./button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "./input-group";

/** A two-cell row's value: the left field, then the right field. */
export type PairFieldValue = [string, string];

/**
 * Per-column configuration for a {@link PairFieldRows} cell. The two columns
 * are configured independently; the `Tranche`/threshold/boost semantics live
 * in the consumer, not here.
 */
export interface PairFieldColumn {
    /** Column header label. */
    label: string;
    /** Unit adornment rendered at the inline-end of the field (e.g. a
     *  TokenChip node or plain text). */
    unit?: React.ReactNode;
    /** Optional leading icon shown before the unit (e.g. a USDv coin). */
    icon?: React.ReactNode;
    /** Placeholder for the field. */
    placeholder?: string;
    /** `inputMode` for the field (default "numeric"). */
    inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
    /** Sanitizes/filters raw input before it reaches `onChange`. */
    sanitize?: (raw: string) => string;
    /** Per-cell invalid predicate — drives the red `aria-invalid` state. */
    invalid?: (value: string) => boolean;
}

interface PairFieldRowsProps {
    /** The rows — each a `[left, right]` tuple of string values. */
    rows: PairFieldValue[];
    onChange: (next: PairFieldValue[]) => void;
    /** Config for the left column. */
    left: PairFieldColumn;
    /** Config for the right column. */
    right: PairFieldColumn;
    /** "+Add" button label. */
    addLabel: string;
    /** aria-label for a row's remove button. */
    removeLabel?: string;
    /** Copy shown when there are no rows. */
    emptyLabel?: string;
    disabled?: boolean;
}

/**
 * PairFieldRows — a vertically-stacked, table-like list of two-field rows.
 * A two-label header sits above a stack of rows (each = two boxed fields +
 * remove X) with an "+Add" button below. Generic over per-column config
 * (label, unit, icon, sanitizer, placeholder, invalid predicate); it knows
 * nothing about tranches or any domain semantics.
 */
export function PairFieldRows({
    rows,
    onChange,
    left,
    right,
    addLabel,
    removeLabel = "Remove row",
    emptyLabel = "No rows yet — add one to start.",
    disabled = false
}: PairFieldRowsProps): React.ReactElement {
    const columns = [left, right];

    const add = (): void => onChange([...rows, ["", ""]]);
    const update = (i: number, col: 0 | 1, val: string): void =>
        onChange(
            rows.map((row, idx) => {
                if (idx !== i) return row;
                const next: PairFieldValue = [row[0], row[1]];
                next[col] = val;
                return next;
            })
        );
    const remove = (i: number): void =>
        onChange(rows.filter((_, idx) => idx !== i));

    return (
        <div className="flex flex-col gap-2 max-w-xl">
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_1fr_2rem] items-center gap-2 px-1">
                <Text variant="label" className="text-foreground-muted">
                    {left.label}
                </Text>
                <Text variant="label" className="text-foreground-muted">
                    {right.label}
                </Text>
                <span />
            </div>

            {rows.length === 0 ? (
                <Text
                    variant="small"
                    className="text-foreground-muted px-1 py-2"
                >
                    {emptyLabel}
                </Text>
            ) : (
                rows.map((row, i) => (
                    <div
                        key={i}
                        className="grid grid-cols-[1fr_1fr_2rem] items-center gap-2"
                    >
                        {columns.map((column, c) => {
                            const col = c as 0 | 1;
                            const value = row[col];
                            // When a column declares an `invalid` predicate we
                            // mirror its boolean onto `aria-invalid` (matching
                            // the boost cell's always-set attribute); columns
                            // without a predicate leave the attribute off.
                            const ariaInvalid = column.invalid
                                ? column.invalid(value)
                                : undefined;
                            return (
                                <InputGroup key={col} className="h-11">
                                    <InputGroupInput
                                        value={value}
                                        inputMode={
                                            column.inputMode ?? "numeric"
                                        }
                                        placeholder={column.placeholder}
                                        disabled={disabled}
                                        aria-invalid={ariaInvalid}
                                        onChange={(e) =>
                                            update(
                                                i,
                                                col,
                                                column.sanitize
                                                    ? column.sanitize(
                                                          e.target.value
                                                      )
                                                    : e.target.value
                                            )
                                        }
                                        numeric
                                        className="text-left"
                                    />
                                    <InputGroupAddon align="inline-end">
                                        <span className="flex items-center gap-1.5">
                                            {column.icon ?? null}
                                            {column.unit !== undefined ? (
                                                <Text
                                                    variant="fine-print"
                                                    render={<span />}
                                                    className="text-foreground-neutral"
                                                >
                                                    {column.unit}
                                                </Text>
                                            ) : null}
                                        </span>
                                    </InputGroupAddon>
                                </InputGroup>
                            );
                        })}
                        <button
                            type="button"
                            onClick={() => remove(i)}
                            disabled={disabled}
                            aria-label={removeLabel}
                            className="flex size-8 items-center justify-center text-error-base transition-opacity hover:opacity-70"
                        >
                            <CrossIcon className="size-6" />
                        </button>
                    </div>
                ))
            )}

            <Button
                variant="sunken"
                outline
                interaction="lifted"
                size="sm"
                onClick={add}
                disabled={disabled}
                className="self-start"
            >
                {addLabel}
            </Button>
        </div>
    );
}
